import React, { useContext, useState } from "react";
import styled from "styled-components";
import { db } from "../firebase/firebaseConfig";
import {
  collection,
  addDoc,
  Timestamp,
  updateDoc,
  doc,
} from "firebase/firestore";
import { toast } from "react-toastify";
import { UserContext } from "../context/UserContext";

const ReferralJobForm = () => {
  const { user } = useContext(UserContext);
  const [formData, setFormData] = useState({
    jobTitle: "",
    companyName: "",
    jobLocation: "",
    jobType: "Full-time",
    experience: "",
    description: "",
    referralEmail: user?.email || "",
    referralLinkedIn: "",
    referralCode: "",
  });

  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ✅ Validate required fields
    if (
      !formData.jobTitle ||
      !formData.companyName ||
      !formData.jobLocation ||
      !formData.jobType ||
      !formData.experience ||
      !formData.description ||
      !formData.referralEmail ||
      !formData.referralCode
    ) {
      alert("All fields are required!");
      return;
    }

    try {
      setLoading(true);

      // ✅ First add document
      const docRef = await addDoc(collection(db, "referralJobs"), {
        ...formData,
        postedByName: user?.name || "Anonymous",
        postedByEmail: user?.email || formData.referralEmail,
        postedById: user.id,
        createdAt: Timestamp.now(),
      });

      // ✅ Update the same document with its own ID
      await updateDoc(doc(db, "referralJobs", docRef.id), {
        id: docRef.id,
      });

      toast.success("Referral Job Submitted Successfully!");
      setFormData({
        jobTitle: "",
        companyName: "",
        jobLocation: "",
        jobType: "Full-time",
        experience: "",
        description: "",
        referralEmail: user?.email || "",
        referralLinkedIn: "",
        referralCode: "",
      });
    } catch (err) {
      console.error("Error adding job:", err);
      alert("Failed to submit job. Try again!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageWrapper>
      {loading && (
        <LoadingOverlay>
          <Loader></Loader>
        </LoadingOverlay>
      )}
      <FormCard>
        <h2>Post a Referral Job</h2>
        <p className="subtitle">
          Share job openings from your company and help the dev community 🚀
        </p>

        <form onSubmit={handleSubmit}>
          <FormGroup>
            <label>Job Title *</label>
            <input
              type="text"
              name="jobTitle"
              value={formData.jobTitle}
              onChange={handleChange}
              required
            />
          </FormGroup>

          <FormGroup>
            <label>Company Name *</label>
            <input
              type="text"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              required
            />
          </FormGroup>

          <FormGroup>
            <label>Location *</label>
            <input
              type="text"
              name="jobLocation"
              value={formData.jobLocation}
              onChange={handleChange}
              placeholder="Remote / City, Country"
              required
            />
          </FormGroup>

          <FormGroup>
            <label>Job Type *</label>
            <select
              name="jobType"
              value={formData.jobType}
              onChange={handleChange}
              required
            >
              <option value="Full-time">Full-time</option>
              <option value="Part-time">Part-time</option>
              <option value="Internship">Internship</option>
              <option value="Contract">Contract</option>
            </select>
          </FormGroup>

          <FormGroup>
            <label>Experience Required *</label>
            <input
              type="text"
              name="experience"
              value={formData.experience}
              onChange={handleChange}
              placeholder="e.g. 2+ years React, Node.js"
              required
            />
          </FormGroup>

          <FormGroup>
            <label>Job Description *</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
              placeholder="Write short details about the role..."
              required
            />
          </FormGroup>

          <FormGroup>
            <label>Your Contact Email *</label>
            <input
              type="email"
              name="referralEmail"
              value={formData.referralEmail}
              onChange={handleChange}
              required
            />
          </FormGroup>

          <FormGroup>
            <label>Your LinkedIn Profile (Optional)</label>
            <input
              type="url"
              name="referralLinkedIn"
              value={formData.referralLinkedIn}
              onChange={handleChange}
              placeholder="https://linkedin.com/in/username"
            />
          </FormGroup>
          <FormGroup>
            <label>Referral Code *</label>
            <input
              type="text"
              name="referralCode"
              value={formData.referralCode}
              onChange={handleChange}
              placeholder="Enter unique referral code"
              required
            />
          </FormGroup>
          <SubmitButton type="submit" disabled={loading}>
            {loading ? "Submitting..." : "Submit Referral"}
          </SubmitButton>
        </form>
      </FormCard>
    </PageWrapper>
  );
};

export default ReferralJobForm;

/* ---------------- Styled Components ---------------- */
/* ---------------- Styled Components (Upgraded UI) ---------------- */

const PageWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px 16px;
  min-height: 100vh;
  background: radial-gradient(circle at top left, #0b0617, #000);
`;

const LoadingOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const Loader = styled.div`
  border: 6px solid rgba(255, 255, 255, 0.2);
  border-top: 6px solid #9b5cff;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  animation: spin 1s linear infinite;
  box-shadow: 0 0 15px rgba(155, 92, 255, 0.4);

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const FormCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(155, 92, 255, 0.15);
  border-radius: 20px;
  padding: 32px;
  max-width: 650px;
  width: 100%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(14px);
  color: #fff;
  transition: 0.3s all ease;

  &:hover {
    box-shadow: 0 15px 45px rgba(155, 92, 255, 0.25);
  }

  h2 {
    color: #c59fff;
    font-size: 1.6rem;
    font-weight: 700;
    margin-bottom: 6px;
    text-align: center;
    background: linear-gradient(90deg, #9b5cff, #c59fff);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .subtitle {
    text-align: center;
    font-size: 0.9rem;
    color: #bdbdbd;
    margin-bottom: 24px;
  }

  @media (max-width: 768px) {
    padding: 24px;
  }

  @media (max-width: 480px) {
    padding: 18px;
    border-radius: 16px;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 18px;

  label {
    margin-bottom: 6px;
    font-size: 0.9rem;
    font-weight: 500;
    color: #ddd;
  }

  input,
  select,
  textarea {
    padding: 12px;
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(255, 255, 255, 0.08);
    color: #fff;
    font-size: 0.95rem;
    outline: none;
    transition: all 0.25s ease;
    backdrop-filter: blur(8px);
  }

  input::placeholder,
  textarea::placeholder {
    color: #888;
  }

  input:focus,
  textarea:focus,
  select:focus {
    border: 1px solid #9b5cff;
    box-shadow: 0 0 10px rgba(155, 92, 255, 0.3);
  }

  select {
    appearance: none;
    background-image: linear-gradient(45deg, #9b5cff, #c59fff);
    background-clip: text;
    color: #fff;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 14px;
  border-radius: 10px;
  border: none;
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  box-shadow: 0 6px 18px rgba(155, 92, 255, 0.25);
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 34px rgba(155, 92, 255, 0.4);
  }

  &:disabled {
    background: rgba(255, 255, 255, 0.15);
    cursor: not-allowed;
  }
`;
